
<template>
  <div v-if="promoCodes.length > 0" class="bg-white rounded-lg border border-gray-200 shadow-sm">
    <ReusableTable
      :columns="promoCodeColumns"
      :data="promoCodes"
      :sortable="true"
      :show-edit="true"
      :show-delete="false"
    >
      <!-- Colonne personnalisée: Statut -->
      <template #column-statut="{ row }">
        <span :class="['inline-flex items-center px-2 py-1 rounded text-xs font-medium', statusBadgeClass(row)]" :title="statusTitle(row)">
          <i :class="statusIcon(row)" class="mr-1"></i>
          {{ statusLabel(row) }}
        </span>
      </template>
      <!-- Slot pour les actions -->
      <template #actions="{ row }">
        <div class="flex items-center gap-2">
          <label class="switch" :title="row.actif === 'activé' ? 'Désactiver' : 'Activer'">
            <input
              type="checkbox"
              :checked="row.actif === 'activé'"
              @change="handleTogglePromoCodeStatus(row)"
            />
            <span class="slider"></span>
          </label>
          <button
            @click.stop="viewPromoCode(row)"
            class="w-8 h-8 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center justify-center"
            title="Voir"
          >
            <i class="fas fa-eye text-sm"></i>
          </button>
          <!-- Bouton d'envoi email si un acheteur spécifique est défini -->
          <button
            v-if="row.buyerEmail && row.acheteur !== 'Tous les acheteurs'"
            @click.stop="openSendEmail(row)"
            class="w-8 h-8 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center justify-center"
            title="Envoyer un email à cet acheteur"
          >
            <i class="fas fa-envelope text-sm"></i>
          </button>
          <button
            @click.stop="handleDeletePromoCode(row)"
            class="w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center justify-center"
            title="Supprimer"
          >
            <i class="fas fa-trash text-sm"></i>
          </button>
        </div>
      </template>
    </ReusableTable>
  </div>
  
  <!-- Empty state -->
  <div v-else class="bg-white rounded-lg border border-gray-200 p-8 text-center">
    <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <i class="fas fa-ticket-alt text-black text-2xl"></i>
    </div>
    <p class="text-black mb-2">Vous n'avez aucun code promo personnalisé.</p>
    <p class="text-sm text-black">Créez votre premier code promo pour commencer.</p>
  </div>

  <!-- Modal de confirmation de suppression -->
  <ModalComp
    :isVisible="showDeleteModal"
    title="Confirmer la suppression"
    size="md"
    @update:isVisible="showDeleteModal = false"
  >
    <template #body>
      <div class="text-center py-4">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <i class="fas fa-exclamation-triangle text-red-600 text-xl"></i>
        </div>
        <h3 class="text-lg font-medium text-black mb-2">
          Supprimer le code promo
        </h3>
        <p class="text-sm text-black mb-4">
          Êtes-vous sûr de vouloir supprimer le code promo <strong>"{{ promoToDelete?.code }}"</strong> ?
        </p>
        <p class="text-xs text-black">
          Cette action est irréversible.
        </p>
      </div>
    </template>
    
    <template #footer>
      <div class="flex justify-end space-x-3">
        <button
          @click="showDeleteModal = false"
          type="button"
          class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
        >
          Annuler
        </button>
        <button
          @click="confirmDelete"
          type="button"
          class="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none transition-colors"
        >
          Supprimer
        </button>
      </div>
    </template>
  </ModalComp>

  <!-- Modal confirmation envoi email -->
  <ModalComp
    :isVisible="showSendModal"
    title="Envoyer l'email promotionnel"
    size="sm"
    @update:isVisible="showSendModal = false"
  >
    <template #body>
      <div class="text-sm text-black space-y-2">
        <p>
          Confirmer l'envoi de l'email promotionnel à
          <strong>{{ promoToSend?.acheteur }}</strong>
          (<span class="text-black">{{ promoToSend?.buyerEmail }}</span>) ?
        </p>

      </div>
    </template>
    <template #footer>
      <div class="flex justify-end space-x-3">
        <button
          @click="showSendModal = false"
          type="button"
          class="px-4 py-2 text-black bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none transition-colors"
          :disabled="isSendingEmail"
          :class="isSendingEmail ? 'opacity-60 cursor-not-allowed' : ''"
        >
          Annuler
        </button>
        <button
          @click="confirmSend"
          type="button"
          class="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none transition-colors inline-flex items-center gap-2"
          :disabled="isSendingEmail"
          :class="isSendingEmail ? 'opacity-60 cursor-not-allowed' : ''"
        >
          <svg v-if="isSendingEmail" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
          <span>{{ isSendingEmail ? 'Envoi…' : 'Envoyer' }}</span>
        </button>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ReusableTable, ModalComp } from '@/components'
import type { PromoCode, PromoCodeTableProps, PromoCodeTableEmits } from '@/types/promo'
import { sendPromoEmail } from '@/api/promoCodes'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'

defineProps<PromoCodeTableProps>()
const emit = defineEmits<PromoCodeTableEmits>()
const { success, error: showError } = useNotification()
const auth = useAuthStore()

// État de la modal de suppression
const showDeleteModal = ref(false)
const promoToDelete = ref<PromoCode | null>(null)
const showSendModal = ref(false)
const promoToSend = ref<PromoCode | null>(null)
const isSendingEmail = ref(false)

// Table columns
const promoCodeColumns = [
  { key: 'code', title: 'CODE PROMO', sortable: true, align: 'left' as const },
  { key: 'acheteur', title: 'Acheteur', sortable: true, align: 'left' as const },
  { key: 'type', title: 'Type', sortable: true, align: 'left' as const },
  { key: 'montant', title: 'Montant', sortable: true, align: 'right' as const },
  { key: 'montantMin', title: 'Minimum', sortable: true, align: 'right' as const },
  { key: 'dateDebut', title: 'Date début', sortable: true, align: 'left' as const },
  { key: 'dateFin', title: 'Date fin', sortable: true, align: 'left' as const },
  { key: 'statut', title: 'Statut', sortable: false, align: 'left' as const }
]

// ===== Helpers Statut =====
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const parseYmd = (s?: string) => {
  if (!s) return null
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

const statusLabel = (row: PromoCode) => {
  const isManuallyActive = (row.actif || '').toLowerCase() === 'activé'
  if (!isManuallyActive) return 'Désactivé'

  const today = startOfDay(new Date())
  const start = parseYmd(row.dateDebut)
  const end = parseYmd(row.dateFin)

  const startsOk = !start || startOfDay(start) <= today
  const endsOk = !end || today <= startOfDay(end)

  if (startsOk && endsOk) return 'Actif aujourd’hui'
  if (start && startOfDay(start) > today) return 'À venir'
  return 'Expiré'
}

const statusBadgeClass = (row: PromoCode) => {
  const label = statusLabel(row)
  switch (label) {
    case 'Actif aujourd’hui':
      return 'bg-green-100 text-green-800'
    case 'À venir':
      return 'bg-blue-100 text-blue-800'
    case 'Expiré':
      return 'bg-gray-100 text-black'
    default:
      return 'bg-red-100 text-red-700'
  }
}

const statusIcon = (row: PromoCode) => {
  const label = statusLabel(row)
  switch (label) {
    case 'Actif aujourd’hui':
      return 'fas fa-check-circle'
    case 'À venir':
      return 'fas fa-clock'
    case 'Expiré':
      return 'fas fa-hourglass-end'
    default:
      return 'fas fa-ban'
  }
}

const statusTitle = (row: PromoCode) => {
  const start = row.dateDebut || '—'
  const end = row.dateFin || '—'
  return `Période: ${start} → ${end}`
}

const handleDeletePromoCode = (row: PromoCode) => {
  promoToDelete.value = row
  showDeleteModal.value = true
}

const confirmDelete = () => {
  if (promoToDelete.value) {
    emit('delete', promoToDelete.value)
    showDeleteModal.value = false
    promoToDelete.value = null
  }
}

const handleTogglePromoCodeStatus = (row: PromoCode) => {
  emit('toggle-status', row)
}

const viewPromoCode = (row: PromoCode) => {
  emit('view', row)
}

const openSendEmail = (row: PromoCode) => {
  promoToSend.value = row
  showSendModal.value = true
  isSendingEmail.value = false
}

const confirmSend = async () => {
  if (isSendingEmail.value) return
  if (!promoToSend.value?.buyerEmail) {
    showError('Email acheteur introuvable')
    showSendModal.value = false
    return
  }
  try {
    isSendingEmail.value = true
    // Construire les infos nécessaires pour l'email promo
    const buyerFirstName = (promoToSend.value.acheteur || '').split(' ')[0] || 'Client'
    const sellerName = auth.user?.nomBoutique || 'Votre grossiste'
    // discountLabel dérivé du montant/type (ex: "-20%" ou "30€")
    // Conserver les décimales (ex: 1.05% ne doit pas devenir 105%)
    const montantStr = String(promoToSend.value.montant)
    const montantMatch = montantStr.match(/\d+(?:[.,]\d+)?/)
    const montantLabel = (montantMatch ? montantMatch[0] : '').replace(',', '.')
    const discountLabel = promoToSend.value.type === 'Percentage'
      ? `-${montantLabel}%`
      : `${montantLabel}€`
    const promoCode = promoToSend.value.code
    const endDate = promoToSend.value.dateFin || '—'
    const catalogueUrl = window.location.origin

    const ok = await sendPromoEmail({
      to: [promoToSend.value.buyerEmail],
      buyerFirstName,
      sellerName,
      discountLabel,
      promoCode,
      endDate,
      catalogueUrl,
    })
    if (ok) {
      success('Email envoyé avec succès')
    } else {
      showError('Échec de l\'envoi de l\'email')
    }
  } catch (e) {
    showError('Erreur lors de l\'envoi de l\'email')
  } finally {
    isSendingEmail.value = false
    showSendModal.value = false
    promoToSend.value = null
  }
}
</script>

<style scoped>
/* Switch Bootstrap-like */
.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  cursor: pointer;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

input:checked + .slider {
  background-color: #10b981; /* green-500 */
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* Hover effect */
.switch:hover .slider {
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.3);
}

input:checked + .slider:hover {
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
}
</style> 